/**
 * System Entity Descriptors
 *
 * Descriptors for system-level entities that are not organization-specific.
 */
import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { GLOBAL_CONFIG, SESSION } from '../types/system.js';
/**
 * Descriptor for GlobalConfig entity.
 * Stored in the global 'globalConfig' collection.
 */
export const globalConfigDescriptor = {
    name: GLOBAL_CONFIG,
    label: 'Configuração global',
    domain: 'system',
    collectionPath: 'globalConfig',
    version: 1,
    description: 'Global application configuration settings',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Configuration document identifier (e.g., "default")',
        },
        {
            name: 'monitorHost',
            label: 'Host do monitor',
            type: 'string',
            required: false,
            description: 'WebSocket host for the monitoring service',
        },
        {
            name: 'systemApiHost',
            label: 'Host da System API (HTTP)',
            type: 'string',
            required: false,
            description: 'HTTP host for the System API REST endpoints — all REST integrations are built in source from this address',
        },
        {
            name: 'systemApiHostWs',
            label: 'Host da System API (WebSocket)',
            type: 'string',
            required: false,
            description: 'WebSocket host for the System API — drives the WS connection ({host}/ws/session)',
        },
        {
            name: 'consoleUrl',
            label: 'URL do console',
            type: 'string',
            required: false,
            description: 'Public URL of the web console — mobile opens non-native module links here (0312)',
        },
        {
            name: 'defaultPlanIds',
            label: 'Planos padrão para novas orgs',
            type: 'array',
            arrayItemType: 'string',
            required: false,
            description: 'Plan IDs contracted automatically when creating a new organization (0152)',
        },
        {
            name: 'notificationRetentionDays',
            label: 'Retenção de notificações lidas (dias)',
            type: 'number',
            required: false,
            description: 'Days to keep read notifications before purge (0313). Integer 7–3650; absent → 90',
        },
    ],
};
/**
 * Descriptor for Session entity.
 * Stored in the global 'session' collection.
 */
export const sessionDescriptor = {
    name: SESSION,
    label: 'Sessão do usuário',
    domain: 'system',
    collectionPath: 'session',
    version: 1,
    description: 'Tracks user sessions across devices',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Combined session identifier (deviceId_sessionId)',
        },
        {
            name: 'deviceId',
            label: 'ID do dispositivo',
            type: 'string',
            required: true,
            description: 'Unique device identifier',
        },
        {
            name: 'deviceInfo',
            label: 'Info do dispositivo',
            type: 'object',
            required: true,
            description: 'Device metadata (userAgent, platform, etc.)',
        },
        {
            name: 'createdAt',
            label: 'Criado em',
            type: 'date',
            required: true,
            description: 'Session creation timestamp',
        },
        {
            name: 'lastSeenAt',
            label: 'Último acesso em',
            type: 'date',
            required: true,
            description: 'Last activity timestamp',
        },
    ],
};
//# sourceMappingURL=system.descriptor.js.map